import { useId, useState } from 'react';

import { Strings } from '../strings';
import { Type } from '../theme/typography';

/**
 * design-system/06-Components/02-Text-Inputs.md.
 * Mirrors android/…/Components/AppTextField.kt.
 *
 * The label sits above the field and is **always visible** — never a placeholder. A placeholder that
 * disappears on focus takes the question with it, which is exactly wrong for a child composing a sentence.
 * On the web it is a real `<label for>`, so a screen reader gets the same relationship the eye does.
 *
 * A password field carries a reveal control, because a field you cannot read is a field you cannot check —
 * and the one place that matters most is registration, where a typo is not discovered until the first
 * failed sign-in (R0144). It starts hidden, and the button is named for what pressing it does.
 */
export function AppTextField({
  label,
  value,
  onChange,
  isPassword = false,
  inputMode,
  autoComplete,
  type,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  isPassword?: boolean;
  inputMode?: 'text' | 'email' | 'numeric';
  autoComplete?: string;
  type?: string;
}) {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);

  return (
    <div style={{ width: '100%' }}>
      <label htmlFor={id} style={{ ...Type.label, color: 'var(--text-secondary)', display: 'block' }}>
        {label}
      </label>

      <div style={{ position: 'relative', width: '100%' }}>
        <input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          type={type ?? (isPassword && !revealed ? 'password' : 'text')}
          inputMode={inputMode}
          autoComplete={autoComplete}
          style={{
            marginTop: 'var(--space-1)',
            width: '100%',
            minHeight: 'var(--touch-parent)',
            borderRadius: 'var(--radius-m)',
            background: 'var(--surface-raised)',
            border: focused ? '2px solid var(--border-selected)' : '1px solid var(--border-default)',
            padding: focused ? 'calc(var(--space-3) - 1px)' : 'var(--space-3)',
            // After the shorthand, or the shorthand wins. Room for the reveal control, so a long password
            // does not run underneath it.
            paddingInlineEnd: isPassword ? 'var(--touch-parent)' : undefined,
            fontSize: '16px',
            lineHeight: '24px',
            color: 'var(--text-primary)',
          }}
        />

        {isPassword ? (
          <button
            type="button"
            aria-label={revealed ? Strings.passwordHide : Strings.passwordShow}
            aria-pressed={revealed}
            onClick={() => setRevealed((previous) => !previous)}
            style={{
              position: 'absolute',
              insetInlineEnd: 0,
              top: 'var(--space-1)',
              bottom: 0,
              width: 'var(--touch-parent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <EyeIcon crossed={revealed} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * An eye, drawn rather than imported.
 *
 * There is no icon set in this project and no dependency worth adding for one glyph — the Material icon
 * pack is several megabytes of vectors for a shape that is two curves and a circle. Mirrored by
 * `Icons/PasswordVisibility.kt` on Android and by SF Symbols on iOS.
 *
 * `crossed` is the *revealed* state: the slash means "press to hide", matching the button's label.
 */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.8" />
      {crossed ? <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /> : null}
    </svg>
  );
}
